/**
 * Super-admin users console (RBAC FE WS-D) — list / invite / delete / reset-pw SAs.
 *
 * SA-HOST surface. Two invariants are owned here (architect_design_rbac_frontend.md §3):
 *
 *   #4 SA short-circuit — gating reads `useAuthStore.isSuperAdmin` (the JWT
 *      `is_sa` claim), NOT `/auth/me.capabilities`. The page renders for an SA
 *      even before `/auth/me` resolves and even when the backend returns
 *      `capabilities: []` for an SA. Page-level error handling degrades
 *      gracefully on any 403/404 (including a flagged-SA `password_change_required`
 *      403, which the global WS-E interceptor redirects away before we render).
 *
 *   #8 no SA-identity leak under impersonation — while
 *      `useAuthStore.impersonation !== null` the console renders NOTHING that
 *      reveals the SA's identity or SA-only controls. The nav link + route are
 *      also hidden/short-circuited via the SAME explicit impersonation check
 *      (AdminShell nav link; this guard). The backend `require_not_impersonating`
 *      is the real boundary — FE hiding only avoids dead-403 clicks; every write
 *      still handles the 403 path (#7).
 *
 * Frontend gating is COSMETIC, never a security boundary (§1). Every write
 * surfaces the backend's distinct error code verbatim and never collapses
 * `sa_already_exists` / `cannot_delete_self` / `cannot_reset_self` / `not_found`
 * into one message.
 *
 * "Delete" is a HARD delete (founder-directed): after it succeeds the user does
 * not exist anywhere. The row DISAPPEARS and a success toast confirms it.
 */
import { useState } from "react";
import { PageHeader } from "@/components/admin/PageHeader";
import { Button } from "@/components/common/Button";
import { useAuthStore } from "@/stores/useAuthStore";
import { useToastStore } from "@/stores/useToastStore";
import {
  useSuperAdmins,
  type SaUser,
} from "@/hooks/queries/admin/useSuperAdmins";
import { useInviteSuperAdmin } from "@/hooks/queries/admin/useInviteSuperAdmin";
import { useDeleteSuperAdmin } from "@/hooks/queries/admin/useDeleteSuperAdmin";
import { useResetSuperAdminPassword } from "@/hooks/queries/admin/useResetSuperAdminPassword";

const CARD =
  "rounded-xl border border-[var(--p-slate-150)] bg-white p-4 shadow-card dark:border-white/[0.06] dark:bg-surface-card dark:shadow-card-dark";

function joinName(first: string | null, last: string | null): string {
  return `${first ?? ""} ${last ?? ""}`.trim();
}

/** Distinct delete-error copy (#7 — exact codes, never collapsed). */
function deleteErrorCopy(code: string): string {
  switch (code) {
    case "cannot_delete_self":
      return "You can't delete your own super-admin account. Ask another super-admin to do it.";
    case "not_found":
      // Non-enumerating: don't reveal whether the row existed elsewhere.
      return "That super-admin is no longer available.";
    default:
      return "Couldn't delete that super-admin. Please try again.";
  }
}

/** Distinct reset-password-error copy (#7 — exact codes, never collapsed). */
function resetErrorCopy(code: string): string {
  switch (code) {
    case "cannot_reset_self":
      return "You can't reset your own password here. Use the change-password screen instead.";
    case "not_found":
      return "That super-admin is no longer available.";
    default:
      return "Couldn't reset that super-admin's password. Please try again.";
  }
}

/** Distinct invite-error copy (#7 — exact codes, never collapsed). */
function inviteErrorCopy(code: string): string {
  switch (code) {
    case "sa_already_exists":
      return "A super-admin already exists for this email.";
    default:
      return "Couldn't send the invite. Please try again.";
  }
}

export default function AdminSuperAdmins(): JSX.Element {
  // #4 — gate on the JWT claim in the store, NOT on /auth/me capabilities.
  const isSuperAdmin = useAuthStore((s) => s.isSuperAdmin);
  // #8 — hide ALL SA identity/controls while impersonating, via an explicit
  // impersonation check (not merely by SA-ness — an impersonating session is
  // still an SA at heart).
  const impersonation = useAuthStore((s) => s.impersonation);
  const currentUserId = useAuthStore((s) => s.userId);

  // #8 — gate the read OFF while impersonating: the SA-list endpoint would 200
  // with the full SA roster even in an impersonated session (the principal is
  // still the SA), landing SA identity in the impersonated cache. The
  // render-layer early-return below stops display; this stops the fetch too.
  const list = useSuperAdmins(impersonation === null);
  const invite = useInviteSuperAdmin();
  const del = useDeleteSuperAdmin();
  const reset = useResetSuperAdminPassword();
  const addToast = useToastStore((s) => s.addToast);

  const [email, setEmail] = useState("");
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [inviteSuccess, setInviteSuccess] = useState<string | null>(null);
  const [confirmTarget, setConfirmTarget] = useState<SaUser | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [resetTarget, setResetTarget] = useState<SaUser | null>(null);
  const [resetError, setResetError] = useState<string | null>(null);

  // #8 — under impersonation the console reveals no SA identity or controls.
  // The nav link is hidden in AdminShell too; this guard makes the route
  // unreachable-in-substance even if reached by URL (#14 — degrade, don't crash).
  if (impersonation !== null) {
    return (
      <div className="space-y-4">
        <PageHeader title="Super admins" />
        <div className={`max-w-lg text-sm text-text-secondary dark:text-brand-100/70 ${CARD}`}>
          Super-admin management is unavailable while impersonating a tenant
          user. Exit impersonation to manage super-admins.
        </div>
      </div>
    );
  }

  // #4 — defensive page-level gate. Routing already wraps this in
  // RequireSuperAdmin, but a non-SA reaching here by any path degrades to the
  // backend's answer rather than a broken shell (#14).
  if (!isSuperAdmin) {
    return (
      <div className="space-y-4">
        <PageHeader title="Super admins" />
        <div className={`max-w-lg text-sm text-text-secondary dark:text-brand-100/70 ${CARD}`}>
          You don't have access to super-admin management.
        </div>
      </div>
    );
  }

  const handleInvite = (e: React.FormEvent) => {
    e.preventDefault();
    setInviteError(null);
    setInviteSuccess(null);
    const trimmed = email.trim();
    if (!trimmed) {
      setInviteError("Enter an email address.");
      return;
    }
    invite.mutate(
      { email: trimmed },
      {
        onSuccess: (result) => {
          setEmail("");
          // Honest expectation-setting: the invited SA receives a TEMPORARY
          // password and must change it at first login (WS-E redirects them to
          // /admin/change-password). Do not over-promise instant access.
          setInviteSuccess(
            `Invite sent to ${result.email}. They'll receive a temporary password and must set a new one at first login.`,
          );
        },
        onError: (err) => {
          setInviteError(inviteErrorCopy(err.error));
        },
      },
    );
  };

  const handleDelete = (target: SaUser) => {
    setDeleteError(null);
    del.mutate(
      { id: target.id },
      {
        onSuccess: () => {
          setConfirmTarget(null);
          // Founder saw no feedback before — make success unmistakable. The list
          // is invalidated by the hook, so the row also DISAPPEARS.
          addToast(`${target.email} was permanently deleted.`, "success");
        },
        onError: (err) => {
          // #7 — surface the distinct backend code; the modal stays open so the
          // SA sees why (e.g. cannot_delete_self) rather than silently closing.
          setDeleteError(deleteErrorCopy(err.error));
        },
      },
    );
  };

  const handleReset = (target: SaUser) => {
    setResetError(null);
    reset.mutate(
      { id: target.id },
      {
        onSuccess: () => {
          setResetTarget(null);
          addToast(
            `Sent ${target.email} a new temporary password. They'll be required to reset it on next login.`,
            "success",
          );
        },
        onError: (err) => {
          // #7 — surface the distinct backend code; the modal stays open.
          setResetError(resetErrorCopy(err.error));
        },
      },
    );
  };

  const items = list.data ?? [];

  return (
    <div className="mx-auto w-full max-w-[1440px] space-y-4">
      <PageHeader title="Super admins" />

      <p className="max-w-2xl text-sm text-text-secondary dark:text-brand-100/70">
        Super-admins have full platform access across every tenant. Invited
        super-admins receive a temporary password by email and must set a new
        one at first login.
      </p>

      {/* Invite form */}
      <form onSubmit={handleInvite} className={`space-y-3 ${CARD}`}>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-end">
          <label className="block flex-1 text-sm">
            <span className="block text-text-secondary dark:text-brand-100/70">
              Invite a super-admin
            </span>
            <input
              type="email"
              value={email}
              onChange={(e) => {
                setEmail(e.target.value);
                setInviteError(null);
                setInviteSuccess(null);
              }}
              placeholder="name@company.com"
              className="mt-1 w-full rounded-md border border-[var(--p-slate-150)] bg-white px-2 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500 dark:border-white/10 dark:bg-surface-canvas dark:text-text-primary dark:focus:ring-brand-teal"
            />
          </label>
          <Button type="submit" loading={invite.isPending}>
            Send invite
          </Button>
        </div>
        {inviteError ? (
          <p className="text-sm text-red-600 dark:text-red-400">{inviteError}</p>
        ) : null}
        {inviteSuccess ? (
          <p className="text-sm text-[var(--p-green-700)] dark:text-[var(--p-green-350)]">
            {inviteSuccess}
          </p>
        ) : null}
      </form>

      {/* List */}
      {list.isLoading ? (
        <div className="text-sm text-text-secondary dark:text-brand-100/70">
          Loading…
        </div>
      ) : list.error ? (
        // #14 — degrade gracefully; render the backend's answer, not a crash.
        <div className="text-sm text-red-600 dark:text-red-400">
          Couldn't load super-admins: {list.error.message}
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-[var(--p-slate-150)] bg-white shadow-card dark:border-white/[0.06] dark:bg-surface-card dark:shadow-card-dark">
          <table className="w-full text-sm">
            <thead className="bg-surface-canvas text-left text-text-secondary dark:bg-white/[0.03] dark:text-brand-100/60">
              <tr>
                <th className="px-4 py-2 font-medium">Name</th>
                <th className="px-4 py-2 font-medium">Email</th>
                <th className="px-4 py-2 font-medium">Status</th>
                <th className="px-4 py-2 text-right" />
              </tr>
            </thead>
            <tbody>
              {items.length === 0 ? (
                <tr>
                  <td
                    colSpan={4}
                    className="px-4 py-6 text-center text-accent-primary dark:text-brand-100/40"
                  >
                    No super-admins yet.
                  </td>
                </tr>
              ) : (
                items.map((sa) => {
                  const isSelf = sa.id === currentUserId;
                  const name = joinName(sa.first_name, sa.last_name);
                  return (
                    <tr
                      key={sa.id}
                      className="border-t border-brand-100 transition-colors hover:bg-brand-50 dark:border-white/[0.04] dark:hover:bg-white/[0.03]"
                    >
                      <td className="px-4 py-2 font-medium text-text-primary dark:text-text-primary">
                        {name || "—"}
                        {isSelf ? (
                          <span className="ml-2 rounded-full bg-brand-100 px-2 py-0.5 text-[11px] text-[var(--p-ink-700)] ring-1 ring-[var(--p-border-input-light)] dark:bg-white/[0.06] dark:text-brand-100/60 dark:ring-white/[0.08]">
                            you
                          </span>
                        ) : null}
                      </td>
                      <td className="px-4 py-2 text-text-secondary dark:text-brand-100/80">
                        {sa.email}
                      </td>
                      <td className="px-4 py-2">
                        {sa.requires_password_reset ? (
                          <span className="rounded-full bg-[var(--p-amber-chip-bg)] px-2 py-0.5 text-xs text-[var(--p-amber-800)] ring-1 ring-[var(--p-amber-chip-ring)]/50 dark:bg-[var(--p-amber-chip-ring)]/10 dark:text-[var(--p-amber-chip-ring)]">
                            pending first login
                          </span>
                        ) : (
                          <span className="rounded-full bg-brand-positive/10 px-2 py-0.5 text-xs text-[var(--p-green-700)] ring-1 ring-brand-positive/30 dark:bg-brand-positive/15 dark:text-[var(--p-green-350)]">
                            active
                          </span>
                        )}
                      </td>
                      <td className="px-4 py-2 text-right">
                        <div className="flex items-center justify-end gap-3">
                          <button
                            type="button"
                            disabled={isSelf}
                            // Cosmetic disablement only (#7) — the authoritative
                            // gate is the backend `cannot_reset_self` 400.
                            title={
                              isSelf
                                ? "Use the change-password screen for your own account"
                                : undefined
                            }
                            onClick={() => {
                              setResetError(null);
                              setResetTarget(sa);
                            }}
                            className="text-sm font-medium text-brand-600 hover:underline disabled:cursor-not-allowed disabled:text-text-secondary disabled:no-underline dark:text-brand-teal dark:disabled:text-brand-100/40"
                          >
                            Reset password
                          </button>
                          <button
                            type="button"
                            disabled={isSelf}
                            // Cosmetic disablement only (#7) — the authoritative
                            // gate is the backend `cannot_delete_self` 400.
                            title={
                              isSelf
                                ? "You can't delete your own account"
                                : undefined
                            }
                            onClick={() => {
                              setDeleteError(null);
                              setConfirmTarget(sa);
                            }}
                            className="text-sm font-medium text-red-600 hover:underline disabled:cursor-not-allowed disabled:text-text-secondary disabled:no-underline dark:text-red-400 dark:disabled:text-brand-100/40"
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Delete confirmation (HARD delete) */}
      {confirmTarget ? (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        >
          <div className={`max-w-md ${CARD}`}>
            <h2 className="mb-2 text-base font-semibold text-text-primary dark:text-text-primary">
              Delete super-admin?
            </h2>
            <p className="mb-4 text-sm text-text-secondary dark:text-brand-100/70">
              {confirmTarget.email} will be permanently deleted and lose all
              access. This cannot be undone.
            </p>
            {deleteError ? (
              <p className="mb-3 text-sm text-red-600 dark:text-red-400">
                {deleteError}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setConfirmTarget(null);
                  setDeleteError(null);
                }}
              >
                Cancel
              </Button>
              <Button
                variant="danger"
                loading={del.isPending}
                onClick={() => handleDelete(confirmTarget)}
              >
                Delete
              </Button>
            </div>
          </div>
        </div>
      ) : null}

      {/* Reset-password confirmation */}
      {resetTarget ? (
        <div
          role="dialog"
          aria-modal="true"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
        >
          <div className={`max-w-md ${CARD}`}>
            <h2 className="mb-2 text-base font-semibold text-text-primary dark:text-text-primary">
              Reset password?
            </h2>
            <p className="mb-4 text-sm text-text-secondary dark:text-brand-100/70">
              Send {resetTarget.email} a new temporary password? They'll be
              required to reset it on next login.
            </p>
            {resetError ? (
              <p className="mb-3 text-sm text-red-600 dark:text-red-400">
                {resetError}
              </p>
            ) : null}
            <div className="flex justify-end gap-2">
              <Button
                variant="ghost"
                onClick={() => {
                  setResetTarget(null);
                  setResetError(null);
                }}
              >
                Cancel
              </Button>
              <Button
                loading={reset.isPending}
                onClick={() => handleReset(resetTarget)}
              >
                Send temporary password
              </Button>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
